
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllResponses, getUniqueAssessmentNames, ResultsResponse } from '@/services/allResponsesService';

export function useResults() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState('All Assessments');

  // Fetch all responses
  const { 
    data: allResults = [], 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['all-responses'],
    queryFn: getAllResponses,
  });

  // Fetch assessment names for filter
  const { data: assessmentNames = [] } = useQuery({
    queryKey: ['assessment-names'],
    queryFn: getUniqueAssessmentNames,
  });

  // Filter and search results
  const filteredResults = useMemo(() => {
    let filtered = allResults;

    // Filter by assessment
    if (selectedAssessment !== 'All Assessments') {
      filtered = filtered.filter(result => result.assessmentName === selectedAssessment);
    }

    // Search functionality
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(result => 
        result.respondentName.toLowerCase().includes(searchLower) ||
        result.respondentEmail.toLowerCase().includes(searchLower) ||
        result.company.toLowerCase().includes(searchLower) ||
        result.assessmentName.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allResults, selectedAssessment, searchTerm]);

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredResults.length === 0) return;
    
    // Create CSV headers
    const headers = ['Respondent Name', 'Email', 'Company', 'Assessment', 'Submission Date', 'Average Score'];
    
    // Create CSV rows
    const rows = filteredResults.map(result => [
      `"${result.respondentName}"`,
      `"${result.respondentEmail}"`,
      `"${result.company}"`,
      `"${result.assessmentName}"`,
      `"${new Date(result.submittedAt).toLocaleDateString()}"`,
      result.averageScore
    ]);
    
    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    
    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `assessment-results-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return {
    results: filteredResults,
    allResults,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    selectedAssessment,
    setSelectedAssessment,
    assessmentNames: ['All Assessments', ...assessmentNames],
    exportToCSV
  };
}
